import type { ClientLimits } from '../api/types';
import { encodeDrawing, releaseCanvas } from './profilePhoto';

/**
 * Cropping and blurring a photograph before upload (Q1): the staff app's answer to a group photo in which only
 * one child may be recognised.
 *
 * Everything happens on the phone. The edits are a list, in the photograph's own pixels, so undo is dropping
 * the last one and the preview and the upload are drawn by the same function. Only the drawing is uploaded:
 * the photograph as it was taken never leaves the device, and re-encoding it drops its metadata.
 *
 * A blurred area is not filtered but rebuilt from a coarse copy of the photograph, `blurCellsPerEdge` cells
 * across its longest side, smoothed back up. That bounds what the area can show, whatever the browser: a
 * Gaussian blur can be partly undone, and `CanvasRenderingContext2D.filter` is missing from Safari.
 */

/** The editor's numbers, as the API configures them (`photoEditor` in `GET /config/limits`). */
export type PhotoEditorLimits = ClientLimits['photoEditor'];

/** A point in the photograph's own pixels. */
export interface Point {
  x: number;
  y: number;
}

/** A rectangle in the photograph's own pixels. */
export interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** One edit. A later crop replaces an earlier one; blurs add up. */
export type PhotoEdit =
  | { kind: 'crop'; area: Area }
  | { kind: 'blurArea'; area: Area }
  | { kind: 'blurStroke'; points: Point[]; width: number };

/** The tools, one per kind of edit. */
export type PhotoEditTool = PhotoEdit['kind'];

/** A decoded photograph and its size in pixels. */
export interface EditSource {
  image: CanvasImageSource;
  width: number;
  height: number;
}

/** Where a drawing starts in the photograph, and how much it is scaled. */
export interface EditView {
  area: Area;
  scale: number;
}

/** Makes a blank canvas. Tests replace it; the browser's is the default. */
export type CanvasFactory = (width: number, height: number) => HTMLCanvasElement;

/** JPEG quality, as for a chat picture: no visible blocking, and a phone photograph leaves at a few megabytes. */
const JPEG_QUALITY = 0.85;

/** JPEG has no transparency, so a transparent PNG is flattened onto white rather than black. */
const BACKGROUND = '#ffffff';

/** A mask is drawn opaque; only its alpha matters, so any colour does. */
const MASK = '#000000';

const longestSide = (width: number, height: number) => Math.max(width, height);

const clamp = (value: number, low: number, high: number) => Math.min(high, Math.max(low, value));

/** The brush's width in the photograph's pixels: `brushPercent` of its longest side, at least one pixel. */
export function brushWidth(width: number, height: number, limits: PhotoEditorLimits): number {
  return Math.max(1, Math.round((longestSide(width, height) * limits.brushPercent) / 100));
}

/**
 * The side of one blur cell in the photograph's pixels. Counted on the whole photograph, never on a crop, so
 * cropping close to a face before blurring it keeps no more of that face.
 */
export function blurCell(width: number, height: number, limits: PhotoEditorLimits): number {
  return Math.max(1, Math.ceil(longestSide(width, height) / limits.blurCellsPerEdge));
}

/** Where a pointer is on the photograph, from its position on the element showing it, kept inside the photograph. */
export function imagePoint(
  clientX: number,
  clientY: number,
  box: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>,
  width: number,
  height: number,
): Point {
  if (box.width === 0 || box.height === 0) return { x: 0, y: 0 };
  return {
    x: clamp(((clientX - box.left) / box.width) * width, 0, width),
    y: clamp(((clientY - box.top) / box.height) * height, 0, height),
  };
}

/**
 * The rectangle two corners span, in either order, kept inside the photograph and on its whole pixels. A crop
 * that starts or ends between two pixels is drawn by resampling the photograph, which halves every hard edge in it.
 */
export function areaBetween(a: Point, b: Point, width: number, height: number): Area {
  const left = Math.round(clamp(Math.min(a.x, b.x), 0, width));
  const top = Math.round(clamp(Math.min(a.y, b.y), 0, height));
  const right = Math.round(clamp(Math.max(a.x, b.x), 0, width));
  const bottom = Math.round(clamp(Math.max(a.y, b.y), 0, height));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

/** Whether a drag made an area, or was a tap: anything narrower than the brush on either side is a tap. */
export function isArea(area: Area, minimum: number): boolean {
  return area.width >= minimum && area.height >= minimum;
}

/** The part of the photograph kept: the last crop, or all of it. */
export function cropOf(edits: readonly PhotoEdit[], width: number, height: number): Area {
  for (let i = edits.length - 1; i >= 0; i--) {
    const edit = edits[i]!;
    if (edit.kind === 'crop') return edit.area;
  }
  return { x: 0, y: 0, width, height };
}

/** The size an edited photograph is encoded at: the crop, scaled down to `maximumEdge`, never up. */
export function editedSize(crop: Area, maximumEdge: number): { width: number; height: number; scale: number } {
  const scale = Math.min(1, maximumEdge / longestSide(crop.width, crop.height));
  return {
    width: Math.max(1, Math.round(crop.width * scale)),
    height: Math.max(1, Math.round(crop.height * scale)),
    scale,
  };
}

const defaultCanvas: CanvasFactory = (width, height) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

function context2d(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser cannot draw on a canvas.');
  return context;
}

/** Draws every blur's shape, opaque, in the photograph's pixels: the mask the coarse copy shows through. */
function traceBlurs(context: CanvasRenderingContext2D, blurs: readonly PhotoEdit[]): void {
  context.fillStyle = MASK;
  context.strokeStyle = MASK;
  context.lineCap = 'round';
  context.lineJoin = 'round';

  for (const blur of blurs) {
    if (blur.kind === 'blurArea') {
      context.fillRect(blur.area.x, blur.area.y, blur.area.width, blur.area.height);
    } else if (blur.kind === 'blurStroke') {
      const [first, ...rest] = blur.points;
      if (!first) continue;
      context.beginPath();
      if (rest.length === 0) {
        // A stroke of one point is a tap. A canvas draws nothing for a line of no length, so it is a dot.
        context.arc(first.x, first.y, blur.width / 2, 0, Math.PI * 2);
        context.fill();
      } else {
        context.lineWidth = blur.width;
        context.moveTo(first.x, first.y);
        for (const point of rest) context.lineTo(point.x, point.y);
        context.stroke();
      }
    }
  }
}

/**
 * Draws `view.area` of the photograph onto `target`, scaled by `view.scale`, with every blur applied. The
 * target's canvas must already be the size of the area times the scale.
 *
 * Inside a blur, the drawing reads the photograph only through a copy `blurCellsPerEdge` cells across.
 */
export function drawEdited(
  target: CanvasRenderingContext2D,
  source: EditSource,
  edits: readonly PhotoEdit[],
  view: EditView,
  limits: PhotoEditorLimits,
  makeCanvas: CanvasFactory = defaultCanvas,
): void {
  const { area, scale } = view;
  const { width, height } = target.canvas;
  target.drawImage(source.image, area.x, area.y, area.width, area.height, 0, 0, width, height);

  const blurs = edits.filter((edit) => edit.kind !== 'crop');
  if (blurs.length === 0) return;

  const cell = blurCell(source.width, source.height, limits);
  const cellsAcross = Math.max(1, Math.ceil(source.width / cell));
  const cellsDown = Math.max(1, Math.ceil(source.height / cell));
  const coarse = makeCanvas(cellsAcross, cellsDown);
  const layer = makeCanvas(width, height);
  try {
    const coarseContext = context2d(coarse);
    coarseContext.imageSmoothingEnabled = true;
    coarseContext.drawImage(source.image, 0, 0, cellsAcross, cellsDown);

    const layerContext = context2d(layer);
    layerContext.setTransform(scale, 0, 0, scale, -area.x * scale, -area.y * scale);
    traceBlurs(layerContext, blurs);
    layerContext.setTransform(1, 0, 0, 1, 0, 0);

    // Keep the mask's shape, and take its colour from the coarse copy smoothed back up.
    layerContext.globalCompositeOperation = 'source-in';
    layerContext.imageSmoothingEnabled = true;
    const across = cellsAcross / source.width;
    const down = cellsDown / source.height;
    layerContext.drawImage(
      coarse, area.x * across, area.y * down, area.width * across, area.height * down, 0, 0, width, height);

    target.drawImage(layer, 0, 0);
  } finally {
    releaseCanvas(coarse);
    releaseCanvas(layer);
  }
}

/**
 * The edited photograph as a JPEG: the crop, at most `maximumEdge` on its longest side, with every blur.
 *
 * @throws UnreadablePhotoError when the browser cannot draw or encode it.
 */
export function exportEdited(
  source: EditSource,
  edits: readonly PhotoEdit[],
  limits: PhotoEditorLimits,
): Promise<Blob> {
  const crop = cropOf(edits, source.width, source.height);
  const size = editedSize(crop, limits.maximumEdge);
  return encodeDrawing({
    width: size.width,
    height: size.height,
    draw: (context) => {
      context.fillStyle = BACKGROUND;
      context.fillRect(0, 0, size.width, size.height);
      drawEdited(context, source, edits, { area: crop, scale: size.scale }, limits);
    },
  }, 'image/jpeg', JPEG_QUALITY);
}
